import { AppstoreOutlined, DownOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Modal, Radio } from "antd"
import type { MenuProps } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { brand, neutral, semantic, tealGhost } from "./dashboardTokens"
import { homeColor, homeSize, homeText } from "../../home-theme.ts"
import type { DashboardView } from "../../redux/portfolio.types"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

interface SavedViewBarProps {
  views: DashboardView[]
  activeViewId: string | null
  activeView: { id: string; name: string } | null
  defaultViewId: string | null
  isEditing: boolean
  startEdit: () => void
  cancel: () => void
  save: () => Promise<void>
  restoreDefault: () => Promise<void>
  onAddTile: () => void
  onSwitch: (id: string) => Promise<void>
  onCreate: (name: string, from: "current" | "default") => Promise<void>
  onRename: (id: string, name: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onSetDefault: (id: string) => Promise<void>
  /**
   * Home moves Save / Cancel / Reset / Add widget into its own editing bar, so
   * this row keeps only My View and Edit Layout. The legacy `PortfolioDashboard`
   * fallback has no such bar and keeps them here.
   */
  showEditingControls?: boolean
}

export const SavedViewBar = ({
  views,
  activeViewId,
  activeView,
  defaultViewId,
  isEditing,
  startEdit,
  cancel,
  save,
  restoreDefault,
  onAddTile,
  onSwitch,
  onCreate,
  onRename,
  onDelete,
  onSetDefault,
  showEditingControls = true,
}: SavedViewBarProps) => {
  const { t } = useTranslation()
  const [newViewOpen, setNewViewOpen] = useState(false)
  const [newViewName, setNewViewName] = useState("")
  const [newViewFrom, setNewViewFrom] = useState<"current" | "default">("current")
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState("")

  const handleCreateOpen = () => {
    setNewViewName("")
    setNewViewFrom("current")
    setNewViewOpen(true)
  }

  const handleCreateConfirm = async () => {
    if (!newViewName.trim()) return
    await onCreate(newViewName.trim(), newViewFrom)
    setNewViewOpen(false)
  }

  const handleRenameStart = (view: DashboardView) => {
    setRenamingId(view.id)
    setRenameValue(view.name)
  }

  const handleRenameCommit = async (id: string) => {
    if (renameValue.trim()) {
      await onRename(id, renameValue.trim())
    }
    setRenamingId(null)
  }

  const handleDeleteClick = (view: DashboardView) => {
    if (views.length <= 1) return
    Modal.confirm({
      title: t("portfolio.editLayout.deleteTitle", "Delete view"),
      content: t("portfolio.editLayout.deleteConfirm", 'Delete "{{name}}"?', { name: view.name }),
      okText: t("portfolio.editLayout.delete", "Delete"),
      okType: "danger",
      onOk: () => onDelete(view.id),
    })
  }

  const menuItems: MenuProps["items"] = [
    ...views.map((view) => ({
      key: view.id,
      label: (
        <Flex align="center" justify="space-between" gap={8} style={{ minWidth: 180 }}>
          {renamingId === view.id ? (
            <TextInput
              size="small"
              value={renameValue}
              autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onPressEnter={() => void handleRenameCommit(view.id)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setRenamingId(null)
                e.stopPropagation()
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ flex: 1 }}
            />
          ) : (
            <span style={{ flex: 1, color: view.id === activeViewId ? brand.teal : undefined, fontWeight: view.id === activeViewId ? 600 : undefined }}>
              {view.id === activeViewId && !isEditing && <span style={{ marginRight: 6 }}>✓</span>}
              {view.name}
            </span>
          )}
          {isEditing && renamingId !== view.id && (
            <Flex gap={4} align="center">
              <Button
                type="text"
                size="small"
                aria-label={t("portfolio.editLayout.setDefault", "Set as default")}
                onClick={(e) => { e.stopPropagation(); void onSetDefault(view.id) }}
                style={{ color: view.id === defaultViewId ? semantic.star : neutral.faint, padding: "0 2px", minWidth: 0 }}
              >
                {view.id === defaultViewId ? "★" : "☆"}
              </Button>
              <Button
                type="text"
                size="small"
                aria-label={t("portfolio.editLayout.rename", "Rename")}
                onClick={(e) => { e.stopPropagation(); handleRenameStart(view) }}
                style={{ color: neutral.muted, padding: "0 2px", minWidth: 0 }}
              >
                ✎
              </Button>
              <Button
                type="text"
                size="small"
                aria-label={t("portfolio.editLayout.delete", "Delete")}
                onClick={(e) => { e.stopPropagation(); handleDeleteClick(view) }}
                style={{ color: neutral.muted, padding: "0 2px", minWidth: 0 }}
                disabled={views.length <= 1}
              >
                🗑
              </Button>
            </Flex>
          )}
        </Flex>
      ),
    })),
    { type: "divider" },
    {
      key: "new-view",
      label: <span style={{ color: brand.teal }}>{t("portfolio.editLayout.newView", "New view…")}</span>,
    },
  ]

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "new-view") {
      handleCreateOpen()
      return
    }
    if (key !== activeViewId) {
      void onSwitch(key)
    }
  }

  return (
    <>
      <Flex align="center" gap={8} wrap="wrap">
        <span data-guide-target="dashboard-myview-button">
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={["click"]}
          >
            {/* A real application control, not a default button: the view
                picker and Edit Layout are the two things a person looks for
                on this page and both now carry the shell's own 36px height. */}
            <Button
              icon={<AppstoreOutlined />}
              style={{
                height: homeSize.controlHeight,
                borderRadius: homeSize.controlRadius,
                borderColor: "#BBD6DD",
                color: homeColor.teal,
                background: homeColor.tealPale,
                ...homeText.button
              }}
            >
              {activeView?.name ?? t("portfolio.views.myView", "My View")}
              <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        </span>

        {!isEditing && (
          <span data-guide-target="dashboard-edit-layout-button">
            <Button
              icon={<EditOutlined />}
              onClick={startEdit}
              style={{
                height: homeSize.controlHeight,
                borderRadius: homeSize.controlRadius,
                color: homeColor.ink,
                ...homeText.button
              }}
            >
              {t("portfolio.editLayout.edit", "Edit Layout")}
            </Button>
          </span>
        )}

        {showEditingControls && isEditing && (
          <>
            <span data-guide-target="dashboard-add-tile-button">
              <Button size="small" icon={<PlusOutlined />} onClick={onAddTile}>
                {t("portfolio.catalog.addWidget", "Widget")}
              </Button>
            </span>
            <Button size="small" type="primary" onClick={() => void save()}>
              {t("portfolio.editLayout.saveLayout", "Save layout")}
            </Button>
            <Button size="small" onClick={cancel}>
              {t("portfolio.editLayout.cancel", "Cancel")}
            </Button>
            <Button size="small" danger onClick={() => void restoreDefault()}>
              {t("portfolio.editLayout.reset", "Reset to default")}
            </Button>
          </>
        )}
      </Flex>

      <Modal
        open={newViewOpen}
        title={t("portfolio.editLayout.newView", "New view…")}
        onCancel={() => setNewViewOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setNewViewOpen(false)}>
            {t("portfolio.editLayout.cancel", "Cancel")}
          </Button>,
          <Button key="create" type="primary" onClick={() => void handleCreateConfirm()}>
            {t("portfolio.editLayout.create", "Create")}
          </Button>,
        ]}
      >
        <Flex vertical gap="middle">
          <div>
            <div style={{ marginBottom: 4, color: neutral.muted }}>
              {t("portfolio.editLayout.viewName", "View name")}
            </div>
            <TextInput
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              onPressEnter={() => void handleCreateConfirm()}
              autoFocus
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, color: neutral.muted }}>
              {t("portfolio.editLayout.startFrom", "Start from")}
            </div>
            <Radio.Group
              value={newViewFrom}
              onChange={(e) => setNewViewFrom(e.target.value as "current" | "default")}
            >
              <Radio value="current">{t("portfolio.editLayout.fromCurrent", "Current layout")}</Radio>
              <Radio value="default">{t("portfolio.editLayout.fromDefault", "Default")}</Radio>
            </Radio.Group>
          </div>
        </Flex>
      </Modal>
    </>
  )
}
